import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Pagination,
  Button,
  Input,
  Spin,
  Empty,
  AutoComplete,
  Select
} from 'antd';
import CFiltroInicio from './CFiltroInicio';
import CCategoriaInicio from './CCategoriaInicio';
import CCarruselInicio from './CCarruselInicio';
import CCarruselOfertasInicio from './CCarruselOfertasInicio';
import { SearchOutlined, BarcodeOutlined, FilterOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import CAgregarCarrito from './CAgregarCarrito';
import { SucursalContext } from '../../../../context/sucursalContext';
import productoDestacadoImg from '../../../../assets/producto_destacado.png';
import productoOfertaImg from '../../../../assets/producto_oferta.png';

const { Title } = Typography;

/** Altura del Header en MainLayout (misma que layout.js): barra de búsqueda sticky debajo */
const HEADER_HEIGHT = 48;

const CInicio = () => {
  const [articulos, setArticulos] = useState([]);
  const [filters, setFilters] = useState({ asu_habilitado: true, asu_sucursal: 1, ordenar_por: 'relevancia' });
  const [pagination, setPagination] = useState({ current: 1, limit: 16, total: 0 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { sucursal } = useContext(SucursalContext);
  const navigate = useNavigate();
  const location = useLocation();
  const didRestoreRef = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Hydratamos filtros desde:
    // 1) state.returnTo (volver desde detalle)
    // 2) localStorage (volver con botón "Atrás" del navegador)
    if (!sucursal?.suc_id) {
      setReady(false);
      navigate('/', { replace: true });
      return;
    }

    const returnTo = location.state?.returnTo;
    const storedRaw = localStorage.getItem('homeFilters');
    const stored = storedRaw ? JSON.parse(storedRaw) : null;

    const incomingFilters = returnTo?.filters ?? stored?.filters;
    if (incomingFilters) {
      if (returnTo?.filters) didRestoreRef.current = true;

      const nextFilters = {
        ...incomingFilters,
        // Asegurar que siempre use la sucursal actual del contexto
        asu_sucursal: sucursal.suc_id
      };

      setFilters(nextFilters);

      const paginationCurrent =
        typeof returnTo?.paginationCurrent === 'number'
          ? returnTo.paginationCurrent
          : (typeof stored?.paginationCurrent === 'number' ? stored.paginationCurrent : undefined);
      if (typeof paginationCurrent === 'number') {
        setPagination(prev => ({ ...prev, current: paginationCurrent }));
      }

      if (typeof nextFilters.busqueda_texto === 'string') {
        setSearchValue(nextFilters.busqueda_texto);
      }
    }

    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, sucursal?.suc_id, navigate]);

  // Persistir filtros para que también funcione al volver con "Atrás" del navegador
  useEffect(() => {
    if (!ready) return;
    try {
      localStorage.setItem(
        'homeFilters',
        JSON.stringify({
          filters,
          paginationCurrent: pagination.current
        })
      );
    } catch (e) {
      // Si localStorage está bloqueado, no hacemos nada
    }
  }, [ready, filters, pagination.current]);

  useEffect(() => {
    if (!ready) return;
    fetchArticulos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, filters, pagination.current]);

  // Debug: mostrar cuando cambian los filtros
  useEffect(() => {
    console.log('Filters changed:', filters);
  }, [filters]);

  // Función para obtener sugerencias de autocompletado
  const fetchAutocompleteSuggestions = async (value) => {
    if (!value || value.trim().length < 2 || !sucursal?.suc_id) {
      setAutocompleteOptions([]);
      return;
    }

    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce: esperar 300ms antes de buscar
    searchTimeoutRef.current = setTimeout(async () => {
      setAutocompleteLoading(true);
      try {
        const queryParams = new URLSearchParams({
          q: value.trim(),
          sucursal: sucursal.suc_id
        });

        console.log('Fetching autocomplete:', { q: value.trim(), sucursal: sucursal.suc_id });
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/articulo-sucursal/autocomplete?${queryParams.toString()}`
        );
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const data = await res.json();
        console.log('Autocomplete response:', data);

        if (!data.suggestions || data.suggestions.length === 0) {
          setAutocompleteOptions([]);
          setAutocompleteLoading(false);
          return;
        }

        const options = data.suggestions.map((item) => ({
          value: item.nombre,
          label: (
            <div>
              <div style={{ fontWeight: 500 }}>{item.nombre}</div>
              {item.marca && (
                <div style={{ fontSize: '12px', color: '#888' }}>{item.marca}</div>
              )}
            </div>
          ),
          item: item // Guardar el item completo para usar después
        }));

        setAutocompleteOptions(options);
      } catch (error) {
        console.error('Error al obtener sugerencias:', error);
        setAutocompleteOptions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300);
  };

  // Detectar si el valor es un código de barras
  const isBarcode = (value) => {
    return /^\d{8,13}$/.test(value?.trim() || '');
  };

  const fetchArticulos = async () => {
    setLoading(true);
    const queryParams = new URLSearchParams({
      ...filters,
      page: pagination.current,
      limit: pagination.limit,
      asu_sucursal: sucursal.suc_id
    });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal/sucursal/?${queryParams.toString()}`);
      const data = await res.json();
      setArticulos(data.data ?? []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error al obtener artículos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  /** Al elegir categoría en el drawer, se prioriza ese filtro y se limpia la barra de búsqueda (evita AND con texto previo). */
  const handleCategoriaSeleccionada = (catId) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    if (catId) {
      setSearchValue('');
      setAutocompleteOptions([]);
      setFilters(prev => {
        const { busqueda_texto, aem_nombre, aem_categoria_id, ...rest } = prev;
        return {
          ...rest,
          aem_categoria_id: catId,
          asu_habilitado: true,
          asu_sucursal: sucursal.suc_id,
          ordenar_por: prev.ordenar_por || 'relevancia'
        };
      });
    } else {
      setFilters(prev => {
        const { aem_categoria_id, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleVerDetalle = (item) => {
    navigate('/detalle-articulo-inicio', {
      state: {
        articulo: item,
        returnTo: {
          filters,
          paginationCurrent: pagination.current
        }
      }
    });
  };

  const tieneDescuentoVigente = (asu) => {
    if (
      !asu ||
      !asu.asu_precio_con_descuento ||
      !asu.asu_fecha_d_descuento ||
      !asu.asu_fecha_h_descuento
    ) return false;

    const hoy = new Date();
    const desde = new Date(asu.asu_fecha_d_descuento);
    const hasta = new Date(asu.asu_fecha_h_descuento);

    return hoy >= desde && hoy <= hasta;
  };

  const filtroBusquedaActivo =
    typeof filters.busqueda_texto === 'string' && filters.busqueda_texto.trim() !== '';
  const filtroCategoriaActivo =
    filters.aem_categoria_id != null && filters.aem_categoria_id !== '';
  const filtroMarcaActivo =
    typeof filters.aem_marca === 'string' && filters.aem_marca.trim() !== '';
  const filtroPrecioActivo =
    (filters.precio_min != null && filters.precio_min !== '') ||
    (filters.precio_max != null && filters.precio_max !== '');
  const mostrarCarruselOfertas =
    !filtroBusquedaActivo &&
    !filtroCategoriaActivo &&
    !filtroMarcaActivo &&
    !filtroPrecioActivo;

  return (
    <div
      style={{
        padding: 0,
        ...(isMobile ? { marginLeft: -10, marginRight: -10 } : {})
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '1rem',
          paddingTop: '1rem',
          position: 'sticky',
          top: HEADER_HEIGHT,
          zIndex: 1000,
          paddingLeft: isMobile ? '0.5rem' : '160px', // Espacio para el logo superpuesto solo en escritorio
          paddingRight: isMobile ? '0.5rem' : '1rem',
          boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
        }}
      >
        {/* Contenedor para escritorio: búsqueda centrada, botones a la derecha */}
        {!isMobile ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            justifyContent: 'space-between',
            width: '100%'
          }}>
            {/* Espaciador izquierdo para centrar la búsqueda */}
            <div style={{ flex: '1 1 0', minWidth: 0 }}></div>

            {/* Cuadro de búsqueda centrado */}
            <AutoComplete
              style={{ 
                flex: '0 1 auto',
                minWidth: 0,
                maxWidth: '450px',
                width: '100%'
              }}
              options={autocompleteOptions}
              onSearch={fetchAutocompleteSuggestions}
              onChange={(value) => {
                // Solo actualizar el valor, no buscar aún
                setSearchValue(value);
                if (value && value.trim().length >= 2) {
                  fetchAutocompleteSuggestions(value);
                } else {
                  setAutocompleteOptions([]);
                }
              }}
              onSelect={(value, option) => {
                console.log('Option selected:', { value, option });
                // Si se selecciona una opción del autocompletado, buscar directamente
                const searchTerm = option?.item?.nombre || value;
                console.log('Setting search term:', searchTerm);
                
                // Actualizar el valor del input
                setSearchValue(searchTerm);
                
                // Cerrar el dropdown inmediatamente
                setAutocompleteOptions([]);
                
                // Actualizar filtros y paginación (mantener ordenamiento actual)
                setPagination(p => ({ ...p, current: 1 }));
                setFilters(prev => ({
                  busqueda_texto: searchTerm,
                  asu_habilitado: true,
                  asu_sucursal: sucursal.suc_id,
                  ordenar_por: prev.ordenar_por || 'relevancia' // Mantener ordenamiento
                }));
              }}
              value={searchValue}
              notFoundContent={autocompleteLoading ? <Spin size="small" /> : null}
              filterOption={false} // Desactivar filtrado local, usamos el del servidor
              dropdownMatchSelectWidth={true}
            >
              <Input.Search
                placeholder="¿Qué estás buscando? (nombre, marca, código de barras...)"
                allowClear
                enterButton={
                  <Button
                    type="primary"
                    icon={isBarcode(searchValue) ? <BarcodeOutlined /> : <SearchOutlined />}
                    style={{ backgroundColor: '#01634C', borderColor: '#01634C' }}
                  />
                }
                onSearch={(value) => {
                  console.log('Search button clicked:', value);
                  setPagination(p => ({ ...p, current: 1 }));
                  setFilters(prev => {
                    const { aem_nombre, busqueda_texto, ...rest } = prev;
                    if (!value || !value.trim()) {
                      // Si no hay valor, mantener solo los filtros básicos
                      return {
                        asu_habilitado: true,
                        asu_sucursal: sucursal.suc_id,
                        ordenar_por: prev.ordenar_por || 'relevancia' // Mantener ordenamiento
                      };
                    }
                    // Usar busqueda_texto para búsqueda mejorada
                    return {
                      ...rest,
                      busqueda_texto: value.trim(),
                      asu_habilitado: true,
                      asu_sucursal: sucursal.suc_id,
                      ordenar_por: prev.ordenar_por || 'relevancia' // Mantener ordenamiento
                    };
                  });
                  setSearchValue(value || '');
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  // Si se limpia el input, también limpiar filtros
                  if (!value || value.trim() === '') {
                    setFilters(prev => {
                      const { busqueda_texto, aem_nombre, ...rest } = prev;
                      return {
                        ...rest,
                        asu_habilitado: true,
                        asu_sucursal: sucursal.suc_id,
                        ordenar_por: prev.ordenar_por || 'relevancia' // Mantener ordenamiento
                      };
                    });
                    setAutocompleteOptions([]);
                  } else if (value.trim().length >= 2) {
                    fetchAutocompleteSuggestions(value);
                  }
                }}
              />
            </AutoComplete>

            {/* Botones de categorías y filtros a la derecha */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              flexShrink: 0,
              flex: '1 1 0',
              justifyContent: 'flex-end',
              minWidth: 0
            }}>
              <CCategoriaInicio
                categoriaSeleccionadaId={filters.aem_categoria_id}
                onCategoriaSeleccionada={handleCategoriaSeleccionada}
              />

              <Button
                onClick={() => setDrawerVisible(true)}
                style={{
                  backgroundColor: 'white',
                  borderColor: '#01634C',
                  color: '#01634C',
                  fontWeight: 'bold',
                  borderRadius: 6,
                  minWidth: '120px',
                  width: '120px',
                  justifyContent: 'center',
                  boxSizing: 'border-box'
                }}
              >
                Filtros
              </Button>
            </div>
          </div>
        ) : (
          /* Contenedor para móvil: [Búsqueda] [Categorías] [Filtros] en una fila */
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'space-between',
            flexWrap: 'nowrap'
          }}>
            {/* Cuadro de búsqueda */}
            <AutoComplete
              style={{ 
                flex: '1 1 auto',
                minWidth: 0
              }}
              options={autocompleteOptions}
              onSearch={fetchAutocompleteSuggestions}
              onChange={(value) => {
                setSearchValue(value);
                if (value && value.trim().length >= 2) {
                  fetchAutocompleteSuggestions(value);
                } else {
                  setAutocompleteOptions([]);
                }
              }}
              onSelect={(value, option) => {
                console.log('Option selected:', { value, option });
                const searchTerm = option?.item?.nombre || value;
                console.log('Setting search term:', searchTerm);
                
                setSearchValue(searchTerm);
                setAutocompleteOptions([]);
                
                setPagination(p => ({ ...p, current: 1 }));
                setFilters(prev => ({
                  busqueda_texto: searchTerm,
                  asu_habilitado: true,
                  asu_sucursal: sucursal.suc_id,
                  ordenar_por: prev.ordenar_por || 'relevancia'
                }));
              }}
              value={searchValue}
              notFoundContent={autocompleteLoading ? <Spin size="small" /> : null}
              filterOption={false}
              dropdownMatchSelectWidth={true}
            >
              <Input.Search
                placeholder="¿Qué estás buscando? (nombre, marca, código de barras...)"
                allowClear
                enterButton={
                  <Button
                    type="primary"
                    icon={isBarcode(searchValue) ? <BarcodeOutlined /> : <SearchOutlined />}
                    style={{ backgroundColor: '#01634C', borderColor: '#01634C' }}
                  />
                }
                onSearch={(value) => {
                  console.log('Search button clicked:', value);
                  setPagination(p => ({ ...p, current: 1 }));
                  setFilters(prev => {
                    const { aem_nombre, busqueda_texto, ...rest } = prev;
                    if (!value || !value.trim()) {
                      return {
                        asu_habilitado: true,
                        asu_sucursal: sucursal.suc_id,
                        ordenar_por: prev.ordenar_por || 'relevancia'
                      };
                    }
                    return {
                      ...rest,
                      busqueda_texto: value.trim(),
                      asu_habilitado: true,
                      asu_sucursal: sucursal.suc_id,
                      ordenar_por: prev.ordenar_por || 'relevancia'
                    };
                  });
                  setSearchValue(value || '');
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  if (!value || value.trim() === '') {
                    setFilters(prev => {
                      const { busqueda_texto, aem_nombre, ...rest } = prev;
                      return {
                        ...rest,
                        asu_habilitado: true,
                        asu_sucursal: sucursal.suc_id,
                        ordenar_por: prev.ordenar_por || 'relevancia'
                      };
                    });
                    setAutocompleteOptions([]);
                  } else if (value.trim().length >= 2) {
                    fetchAutocompleteSuggestions(value);
                  }
                }}
              />
            </AutoComplete>

            {/* Botón categorías a la derecha del cuadro de búsqueda */}
            <div style={{ flexShrink: 0 }}>
              <CCategoriaInicio
                categoriaSeleccionadaId={filters.aem_categoria_id}
                onCategoriaSeleccionada={handleCategoriaSeleccionada}
              />
            </div>

            {/* Botón filtros solo con icono a la derecha de categorías */}
            <div style={{ flexShrink: 0 }}>
              <Button
                onClick={() => setDrawerVisible(true)}
                icon={<FilterOutlined />}
                style={{
                  backgroundColor: 'white',
                  borderColor: '#01634C',
                  color: '#01634C',
                  borderRadius: 6,
                  width: '40px',
                  height: '40px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* CARRUSEL */}
      <CCarruselInicio />

      {mostrarCarruselOfertas && <CCarruselOfertasInicio />}

      {/* Barra de ordenamiento y resultados */}
      {!loading && articulos.length > 0 && (
        <>
          <Title
            level={4}
            style={{
              marginTop: 16,
              marginBottom: 8,
              paddingLeft: 4,
              paddingRight: 4,
              fontWeight: 700,
              color: '#FFC107',
              letterSpacing: '0.02em'
            }}
          >
            Destacados
          </Title>
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>Ordenar por:</span>
              <Select
                value={filters.ordenar_por || 'relevancia'}
                onChange={(value) => {
                  setPagination(p => ({ ...p, current: 1 }));
                  setFilters(prev => ({
                    ...prev,
                    ordenar_por: value
                  }));
                }}
                style={{ minWidth: 200 }}
                size="middle"
              >
                <Select.Option value="relevancia">Más relevante</Select.Option>
                <Select.Option value="precio_asc">Precio: menor a mayor</Select.Option>
                <Select.Option value="precio_desc">Precio: mayor a menor</Select.Option>
                <Select.Option value="mas_vendidos">Más vendidos</Select.Option>
                <Select.Option value="nombre_asc">Nombre A-Z</Select.Option>
                <Select.Option value="nombre_desc">Nombre Z-A</Select.Option>
              </Select>
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>
              {pagination.total} {pagination.total === 1 ? 'artículo encontrado' : 'artículos encontrados'}
            </div>
          </div>
        </>
      )}

      {/* CONTENIDO */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Spin size="large" />
        </div>
      ) : articulos.length === 0 ? (
        <div style={{ padding: '2rem' }}>
          <Empty description="No se encontraron artículos" />
        </div>
      ) : (
        <Row
          gutter={isMobile ? [6, 10] : [16, 16]}
          style={{ padding: isMobile ? '0.35rem 0.15rem' : '1rem' }}
        >
          {articulos.map((item, index) => {
            const aem = item.articulo_empresa;
            const asu = item.datos_sucursal;
            const descuentoActivo = tieneDescuentoVigente(asu);
            const porcDesc =
              asu?.asu_porc_descuento != null ? Number(asu.asu_porc_descuento) : null;
            const mostrarTachadoYBadge = descuentoActivo && porcDesc != null && porcDesc > 0;
            const hayDestacado = asu != null && asu.asu_orden_relevancia != null;
            return (
              <Col
                key={index}
                xs={12}
                sm={12}
                md={6}
                lg={6}
                style={{ display: 'flex', minWidth: 0 }}
              >
                <Card
                  hoverable
                  onClick={() => handleVerDetalle(item)}
                  styles={{
                    body: isMobile ? { padding: '10px 8px 12px' } : undefined
                  }}
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%'
                  }}
                  cover={
                    <div style={{ position: 'relative', backgroundColor: '#fff' }}>
                      {asu != null && asu.asu_orden_relevancia != null && (
                        <img
                          src={productoDestacadoImg}
                          alt=""
                          role="presentation"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 2,
                            width: 'auto',
                            maxWidth: isMobile ? '42%' : '38%',
                            maxHeight: isMobile ? 44 : 40,
                            height: 'auto',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                      {descuentoActivo && (
                        <img
                          src={productoOfertaImg}
                          alt=""
                          role="presentation"
                          style={{
                            position: 'absolute',
                            top: 4,
                            left: hayDestacado ? 'auto' : 4,
                            right: hayDestacado ? 4 : 'auto',
                            zIndex: 2,
                            width: 'auto',
                            maxWidth: isMobile ? '42%' : '38%',
                            maxHeight: isMobile ? 44 : 40,
                            height: 'auto',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                      <img
                        alt={aem.aem_nombre}
                        src={aem.aem_imagen || 'https://via.placeholder.com/300x200.png?text=Sin+Imagen'}
                        style={{
                          height: 200,
                          width: '100%',
                          objectFit: 'contain',
                          backgroundColor: '#fff',
                          display: 'block',
                        }}
                      />
                    </div>
                  }
                >
                  {/* Nombre */}
                  <Title 
                    level={5} 
                    style={{ 
                      marginBottom: 4, 
                      height: 48,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '24px'
                    }}
                  >
                    {aem.aem_nombre}
                  </Title>

                  {/* Marca */}
                  <div
                    style={{
                      color: '#888',
                      fontSize: '0.9rem',
                      marginBottom: 8,
                      minHeight: 20, // 👈 altura fija
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {aem.aem_marca || ''}
                  </div>

                  {/* Precio / Descuento */}
                  <div style={{ minHeight: 80, marginTop: 8 }}>
                    {mostrarTachadoYBadge ? (
                      <>
                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#52c41a' }}>
                          ${asu.asu_precio_con_descuento.toLocaleString('es-AR')}
                        </div>
                        <div>
                          <span style={{ textDecoration: 'line-through', fontSize: '0.9rem', color: '#999' }}>
                            ${asu.asu_precioventa.toLocaleString('es-AR')}
                          </span>
                          <span style={{ marginLeft: 8, color: '#52c41a', fontSize: '0.9rem' }}>
                            -{porcDesc}% OFF
                          </span>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        $
                        {descuentoActivo && asu?.asu_precio_con_descuento != null
                          ? asu.asu_precio_con_descuento.toLocaleString('es-AR')
                          : (asu?.asu_precioventa?.toLocaleString('es-AR') ?? 'No disponible')}
                      </div>
                    )}

                    {/* Leyenda precio sin impuestos */}
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#888',
                        marginTop: 4,
                        minHeight: 18 // 👈 espacio fijo
                      }}
                    >
                      {asu.asu_precio_siniva != null
                        ? `Precio sin impuestos nacionales: $${asu.asu_precio_siniva.toLocaleString('es-AR')}`
                        : ''}
                    </div>
                  </div>

                  {/* Botón siempre abajo */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: 'auto',
                      width: '100%',
                      minWidth: 0,
                      paddingLeft: 0,
                      paddingRight: 0
                    }}
                  >
                    <CAgregarCarrito articulo={item} esVistaInicioGrid />
                  </div>
                </Card>

              </Col>
            );
          })}
        </Row>
      )}

      <CFiltroInicio
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        sucursalId={sucursal?.suc_id}
        initialMarca={filters.aem_marca}
        initialPrecioMin={filters.precio_min}
        initialPrecioMax={filters.precio_max}
        onApply={(newFilters) => {
          setPagination(p => ({ ...p, current: 1 }));
          setFilters(prev => ({
            ...prev,
            ...newFilters
          }));
        }}
        onReset={() => {
          setPagination(p => ({ ...p, current: 1 }));
          setFilters({ 
            asu_habilitado: true, 
            asu_sucursal: sucursal.suc_id,
            ordenar_por: 'relevancia' // Mantener ordenamiento por defecto
          });
        }}
      />


      {!loading && (
        <Pagination
          current={pagination.current}
          pageSize={pagination.limit}
          total={pagination.total}
          onChange={handlePageChange}
          style={{ margin: '2rem auto', textAlign: 'center' }}
        />
      )}
    </div>
  );
};

export default CInicio;